import moment from 'moment';
import isNumber from 'lodash/isNumber';
import request from '../utils/request';
import { isJetson } from '../containers/Monitoring/utils';

function buildMonitoringApiPayload(params) {
  const newParams = { ...params };
  
  if (isNumber(params.end)) {
    const seconds = moment.utc(params.end * 1000).format('ss.SSS');
    
    if (seconds === '00.000') {
      newParams.end -= 1;
    }
  }
  
  return newParams;
}

function buildQuery(metric, machine) {
  if (machine && machine !== 'select') {
    return `${metric}{host="${machine}"}`;
  }
  
  return metric;
}

function buildMinOverTimeQuery(metric, machine, step) {
  return `min_over_time(${buildQuery(metric, machine)}[${step}])`;
}

function buildMaxOverTimeQuery(metric, machine, step) {
  return `max_over_time(${buildQuery(metric, machine)}[${step}])`;
}

function buildAvailabilityQuery(metricQuery, maskQuery) {
  return `${metricQuery}*(${maskQuery} == bool 1)`;
}

export function getRestaurants(params) {
  return request({
    url: `/restaurants`,
    method: 'get',
    data: {
      expand: 'hosts',
      ...params,
    },
  });
}

export function getMachines() {
  return request({
    url: `/monitoring/label/host/values`,
    method: 'get',
  });
}

export function getCPUScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `100-(avg(rate(node_cpu_seconds_total${
        machine === 'select' ? '' : `{host="${machine}",mode="idle"}`
      }[2m]))*100)`,
    },
  });
}

export function getCPUCores(machine) {
  let query;
  
  if (isJetson(machine)) {
    const queryParams = ['mode="idle"'];
  
    if (machine && machine !== 'select') {
      queryParams.push(`host="${machine}"`);
    }
    
    query = `count(count(node_cpu_seconds_total{${queryParams.join(',')}}) by (cpu))`;
  } else {
    query = `machine_cpu_cores${machine === 'select' ? '' : `{host="${machine}"}`}`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: { query },
  });
}

export function getOSInfo(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_os_info${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getJetsonBuildInfo(machine) {
  let query = 'jetson_build_info';
  
  if (machine && machine !== 'select') {
    query = `${query}{host="${machine}"}`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query,
    },
  });
}

export function getInternetStatus(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: buildQuery('internet_status', machine),
    },
  });
}

export function getInternetStatusVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: buildMinOverTimeQuery('internet_status', machine, params.step),
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getInternetLatencyVector(machine, params) {
  const latencyQuery = buildMaxOverTimeQuery('internet_latency', machine, params.step);
  const statusQuery = buildMinOverTimeQuery('internet_status', machine, params.step);
  
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: buildAvailabilityQuery(latencyQuery, statusQuery),
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getInternetJitterVector(machine, params) {
  const jitterQuery = buildMaxOverTimeQuery('internet_jitter', machine, params.step);
  const statusQuery = buildMinOverTimeQuery('internet_status', machine, params.step);
  
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: buildAvailabilityQuery(jitterQuery, statusQuery),
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getInternetPacketLoss(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: buildMaxOverTimeQuery('internet_packet_loss', machine, params.step),
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getInternetDNSResolvingStatusVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `min_over_time(${buildQuery('internet_dns_resolving_status', machine)}[${params.step}])`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getInternetDNSResolvingTimeVector(machine, params) {
  const dnsResolvingTimeQuery = buildMaxOverTimeQuery('internet_dns_resolving_time', machine, params.step);
  const dnsResolvingStatusQuery = buildMinOverTimeQuery('internet_dns_resolving_status', machine, params.step);
  
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: buildAvailabilityQuery(dnsResolvingTimeQuery, dnsResolvingStatusQuery),
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getJetsonTemperature(machine) {
  let query = 'jetson_temperature_celsius';
  
  if (machine && machine !== 'select') {
    query = `${query}{host="${machine}"}`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query,
    },
  });
}

export function getJetsonTemperatureVector(machine, params) {
  const selector = buildQuery('jetson_temperature_celsius', machine);
  const query = `max_over_time(${selector}[${params.step}])`;
  
  return request({
    url: '/monitoring/query_range',
    method: 'get',
    data: {
      query,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getTemperatureVector(machine, params) {
  const cpuQuery = buildQuery('node_hwmon_temp_celsius', machine);
  const cpuTemp = `max_over_time(${cpuQuery}[${params.step}])`;
  const cpuHostLabel = machine && machine !== 'select' ? `,host="${machine}"` : '';
  const cpuSensorLabel = `node_hwmon_sensor_label{label="Package id 0"${cpuHostLabel}}`;
  const cpu = `${cpuTemp} * on(host, chip, sensor) group_left(label) ${cpuSensorLabel}`;
  
  const gpuQuery = buildQuery('DCGM_FI_DEV_GPU_TEMP', machine);
  
  // Aggregate by stable GPU identity (host, gpu, UUID, modelName) and drop volatile labels
  // such as `hostname` (the dcgm-exporter container ID). The container ID changes on every
  // restart/redeploy, so without this a single physical GPU shows up as several series,
  // which overlap on the chart around the restart.
  const gpu = `max by (host, gpu, UUID, modelName) (max_over_time(${gpuQuery}[${params.step}]))`;
  
  const cpuWithDevice = `label_replace(${cpu}, "device", "cpu", "", "")`;
  const gpuWithDevice = `label_replace(${gpu}, "device", "gpu", "", "")`;
  const query = `${cpuWithDevice} or ${gpuWithDevice}`;
  
  return request({
    url: '/monitoring/query_range',
    method: 'get',
    data: {
      query,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getTimezone(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_time_zone_offset_seconds${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getSWAPTotal(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_memory_SwapTotal_bytes${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getSWAPFree(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_memory_SwapFree_bytes${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getSWAPScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `((node_memory_SwapTotal_bytes${
        machine === 'select' ? '' : `{host="${machine}"}`
      } - node_memory_SwapFree_bytes${
        machine === 'select' ? '' : `{host="${machine}"}`
      }) / (node_memory_SwapTotal_bytes${machine === 'select' ? '' : `{host="${machine}"}`} )) * 100`,
    },
  });
}

export function getSWAPVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `((node_memory_SwapTotal_bytes${
        machine === 'select' ? '' : `{host="${machine}"}`
      } - node_memory_SwapFree_bytes${machine === 'select' ? '' : `{host="${machine}"}`}))`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getCPUInfo(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_cpu_info{cpu="0"${machine === 'select' ? '' : `,host="${machine}"`}}`,
    },
  });
}

export function getCPUTemperature(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_hwmon_temp_celsius${
        machine === 'select' ? '' : `{host="${machine}"}`
      } * on(chip, sensor) group_left(label) node_hwmon_sensor_label{label="Package id 0"${
        machine === 'select' ? '' : `,host="${machine}"`
      }}`,
    },
  });
}

export function getCPUVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `100 - avg by (host) (rate(node_cpu_seconds_total${
        machine === 'select'
          ? `{mode="idle"}`
          : `{host=${machine.includes('|') ? '~' : ''}"${machine}",mode="idle"}`
      }[2m]))*100`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getAllRAM(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_memory_MemTotal_bytes${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getAvailableRAM(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_memory_MemAvailable_bytes${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getRAMScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `((node_memory_MemTotal_bytes${
        machine === 'select' ? '' : `{host="${machine}"}`
      } - node_memory_MemAvailable_bytes${
        machine === 'select' ? '' : `{host="${machine}"}`
      })/node_memory_MemTotal_bytes${machine === 'select' ? '' : `{host="${machine}"}`})*100`,
    },
  });
}

export function getRAMVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `((node_memory_MemTotal_bytes${
        machine === 'select' ? '' : `{host=${machine.includes('|') ? '~' : ''}"${machine}"}`
      } - node_memory_MemAvailable_bytes${
        machine === 'select' ? '' : `{host=${machine.includes('|') ? '~' : ''}"${machine}"}`
      })${
        machine === 'select' || machine.includes('|')
          ? `/node_memory_MemTotal_bytes${machine.includes('|') ? `{host=~"${machine}"}` : ''})*100`
          : ')'
      }`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getAllGPU(machine) {
  let query;
  
  if (isJetson(machine)) {
    const queryParams = ['type="total"'];
  
    if (machine && machine !== 'select') {
      queryParams.push(`host="${machine}"`);
    }
    
    query = `jetson_memory_usage_bytes{${queryParams.join(',')}}`;
  } else {
    query = `DCGM_FI_DEV_FB_FREE${machine === 'select' ? '' : `{host="${machine}"}`}+DCGM_FI_DEV_FB_USED${
      machine === 'select' ? '' : `{host="${machine}"}`
    }`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query,
    },
  });
}

export function getGPUTemperature(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `DCGM_FI_DEV_GPU_TEMP${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}

export function getAvailableGPU(machine) {
  let query;
  
  if (isJetson(machine)) {
    const queryParamsUsed = ['type="used"'];
    const queryParamsTotal = ['type="total"'];
  
    if (machine && machine !== 'select') {
      queryParamsUsed.push(`host="${machine}"`);
      queryParamsTotal.push(`host="${machine}"`);
    }
  
    const used = `jetson_memory_usage_bytes{${queryParamsUsed.join(',')}}`;
    const total = `jetson_memory_usage_bytes{${queryParamsTotal.join(',')}}`;
    
    query = `(${total} - on(host) ${used})`;
  } else {
    query = `DCGM_FI_DEV_FB_FREE${machine === 'select' ? '' : `{host="${machine}"}`}`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query,
    },
  });
}

export function getGPUScalar(machine) {
  let query;
  
  if (isJetson(machine)) {
    const queryParamsUsed = ['type="used"'];
    const queryParamsTotal = ['type="total"'];
  
    if (machine && machine !== 'select') {
      queryParamsUsed.push(`host="${machine}"`);
      queryParamsTotal.push(`host="${machine}"`);
    }
  
    const used = `jetson_memory_usage_bytes{${queryParamsUsed.join(',')}}`;
    const total = `jetson_memory_usage_bytes{${queryParamsTotal.join(',')}}`;
  
    query = `(${used} / on(host) ${total}) * 100`;
  } else {
    query = `DCGM_FI_DEV_FB_USED${machine === 'select' ? '' : `{host="${machine}"}`}/(DCGM_FI_DEV_FB_FREE${
      machine === 'select' ? '' : `{host="${machine}"}`
    }+DCGM_FI_DEV_FB_USED${machine === 'select' ? '' : `{host="${machine}"}`})*100`;
  }
  
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query,
    },
  });
}

export function getGPUVector(machine, params) {
  let query;
  
  if (isJetson(machine)) {
    query = 'jetson_gpu_memory_usage_bytes';
  
    if (machine && machine !== 'select') {
      query = `${query}{host="${machine}"}`;
    }
  } else {
    query = `(DCGM_FI_DEV_FB_USED${
      machine === 'select' ? '' : `{host=${machine.includes('|') ? '~' : ''}"${machine}"}`
    }${
      machine === 'select' || machine.includes('|')
        ? `/(DCGM_FI_DEV_FB_FREE+DCGM_FI_DEV_FB_USED${
          machine.includes('|') ? `{host=~"${machine}"}` : ''
        }))*100`
        : ')'
    }`;
  }
  
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getGPUUtilVector(machine, params) {
  const metrics = [];
  
  if (isJetson(machine)) {
    metrics.push('jetson_gpu_usage_percent');
  } else {
    metrics.push('DCGM_FI_DEV_GPU_UTIL');
    metrics.push('DCGM_FI_DEV_MEM_COPY_UTIL');
    metrics.push('DCGM_FI_DEV_ENC_UTIL');
    metrics.push('DCGM_FI_DEV_DEC_UTIL');
  }
  
  const metricQuery = metrics.join('|');
  const hostFilter = machine === 'select' ? '' : `, host=${machine.includes('|') ? '~' : ''}"${machine}"`;
  
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `{__name__=~"${metricQuery}"${hostFilter}}`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getAllStorage(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host="${machine}"`
      }}`,
    },
  });
}

export function getAvailableStorage(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_filesystem_free_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `, host="${machine}"`
      }}`,
    },
  });
}

export function getStorageScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `((node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host="${machine}"`
      }}-node_filesystem_free_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host="${machine}"`
      }})/node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host="${machine}"`
      }})*100`,
    },
  });
}

export function getStorageVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `((node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }}-node_filesystem_free_bytes{mountpoint="/",fstype!="rootfs"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }})${
        machine === 'select' || machine.includes('|')
          ? `/node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"${
              machine.includes('|') ? `,host=~"${machine}"` : ''
            }})*100`
          : ')'
      }`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getStorageReadsVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `irate(node_disk_read_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }}[2m])`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getStorageWritesVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `irate(node_disk_written_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }}[2m])`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getReceiveNetworkScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `irate(node_network_receive_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host="${machine}"`
      }}[2m])`,
    },
  });
}

export function getTransmitNetworkScalar(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `irate(node_network_transmit_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host="${machine}"`
      }}[2m])`,
    },
  });
}

export function getReceiveNetworkVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `irate(node_network_receive_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }}[2m])`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getTransmitNetworkVector(machine, params) {
  return request({
    url: `/monitoring/query_range`,
    method: 'get',
    data: {
      query: `irate(node_network_transmit_bytes_total{device!="lo"${
        machine === 'select' ? '' : `,host=${machine.includes('|') ? '~' : ''}"${machine}"`
      }}[2m])`,
      ...buildMonitoringApiPayload(params),
    },
  });
}

export function getUpTime(machine) {
  return request({
    url: `/monitoring/query`,
    method: 'get',
    data: {
      query: `node_time_seconds-node_boot_time_seconds${machine === 'select' ? '' : `{host="${machine}"}`}`,
    },
  });
}
